<template>
  <div class="mailbox-verification-instructions-zoho">
    <el-progress
      :percentage="stepPercentage"
      :show-text="false"
      class="progress-bar"
      color="#21b963"
    />
    <el-carousel
      ref="carousel"
      :autoplay="false"
      :loop="false"
      direction="vertical"
      indicator-position="none"
      arrow="always"
      @change="(val) => updateStep(val)">
      <el-carousel-item>
        <h3>
          <img class="logo" src="../logos/zoho-logo.svg">
          <a href="https://www.zoho.com/mail/login.html" target="_blank" class="just-a-link">Click here to sign in to Zoho</a> as {{forwardingFromAddress}}
        </h3>
        <p>
          After you've signed in, <strong>come back to this tab</strong> and click the button below.
        </p>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've signed in to Zoho</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>
          <a href="https://mail.zoho.com/zm/#settings/all/mailaccounts" target="_blank" class="just-a-link">Click here</a> to go to Mail Accounts.
        </h3>
        <p>
          You'll set up your auto-forwarding rule there.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I'm there!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Copy this email address by clicking on it.</h3>
        <i class="ddi ddi-arrow-down bouncy" />
        <clipboard-text :text="incomingEmailAddress" class="incoming-email-address" @click="emailCopied=true" />
        <p><br></p>
        <p>
          This is the email address for this mailbox. In a moment, you'll paste it into Zoho so it knows to 
          auto-forward future emails here.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div v-if="emailCopied" class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">Continue to the next step!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Add the forwarding address to Zoho.</h3>
        <div class="step-wrapper">
          <div class="step">
            <span>1</span>
            <p>Select the <strong>{{ forwardingFromAddress }}</strong> account.</p>
          </div> 
          <div class="step">
            <span>2</span>
            <p>Click the <strong>Forwards</strong> tabs on the right.</p>
          </div>
          <div class="step">
            <span>3</span>
            <p>
              Paste the copied address into the "Add New Email Forwarding" field and hit <strong>Enter</strong>. 
              <em>If you have any existing forwarding addresses below the field, remove those first</em>.
            </p>
          </div>
        </div> 
        <img src="./img/mb-zoho-1.jpg">
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've added the forwarding address.</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <h3>Send a confirmation code.</h3>
        <p>
          Click the <strong>Verify</strong> button next to the forwarding address you just added.
          You will see a pop-up asking for a confirmation code that will be sent to this mailbox. Leave that open!
        </p>
        <p><br></p>
        <img src="./img/mb-zoho-2.jpg">
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button type="flat" @click="goToNextItem">I've done this. Find my confirmation code!</dd-button>
        </div>
      </el-carousel-item>
      <el-carousel-item>
        <mailbox-verification-zoho-confirmation-code-finder
          v-if="isConfirmationEmailStep"
          @go-back-clicked="goToFirstItem"
          @confirmed="goToNextItem"
        />
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
      </el-carousel-item>
      <el-carousel-item>
        <h3>You're ready to test auto-forwarding!</h3>
        <p>
          Now that you've completed all the previous steps, we'll run a quick test to see that
          emails from <strong>{{forwardingFromAddress}}</strong> are forwarding to this mailbox.
        </p>
        <dd-button class="prev-step-button" type="text" @click="goToPrevItem"><i class="ddi ddi-caret-up" /></dd-button>
        <div class="bottom-centered">
          <dd-button :color="ColorAssistant.MailboxColor" @click="$emit('completion-clicked')">Test that auto-forwarding is working!</dd-button>
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>


<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import MailboxVerificationZohoConfirmationCodeFinder from './mailbox-verification-zoho-confirmation-code-finder.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */

export default defineComponent({
  name: 'MailboxVerificationInstructionsZoho',
  components: {
    MailboxVerificationZohoConfirmationCodeFinder,
  },
  props: {
    incomingEmailAddress: {
      type: String,
      required: true,
    },
    forwardingFromAddress: {
      type: String,
      required: true,
    },
  },
  emits: ['completion-clicked'],
  data() {
    return {
      ColorAssistant,
      curStep: 1,
      stepCount: 7,
      emailCopied: false,
    }
  },
  computed: {
    stepPercentage(): number {
      return Math.round(((this.curStep - 1) / (this.stepCount - 1)) * 100)
    },
    isConfirmationEmailStep(): boolean {
      return this.curStep === 6
    },
  },
  methods: {
    updateStep(curItem: any) {
      this.curStep = curItem + 1
      // Reset copied email...
      this.emailCopied = false
    },
    goToNextItem() {
      // @ts-ignore
      this.$refs.carousel.setActiveItem(this.curStep)
    },
    goToPrevItem() {
      // @ts-ignore
      this.$refs.carousel.setActiveItem(this.curStep - 2)
    },
    goToFirstItem() {
      // @ts-ignore
      this.$refs.carousel.setActiveItem(0)
    },
  },
})
</script>

<style lang="scss">
.mailbox-verification-instructions-zoho {
  h3 {
    position: relative;

    > img.logo {
      position: absolute;
      top: -50px;
      border: none !important;
      height: 28px;
      margin: 0 auto;
      left: 0;
      right: 0;
    }
  }
}
</style>
